import { isAppClient } from '@/lib/LocalStorage';
import { appRPC } from '@/lib/utils';
import { safeLocalStorage, safeSessionStorage } from '@/lib/safeStorage';

type RecoverReason =
  | 'global-error-boundary'
  | 'root-error-boundary'
  | 'window.onerror'
  | 'unhandledrejection'
  | 'chunk-load'
  | 'next-router-state'
  | 'react-310'
  | 'unknown';

type RecoverOptions = {
  /** bypass 3x modal gate */
  force?: boolean;
};

function normalizeReason(input: unknown): RecoverReason {
  switch (input) {
    case 'global-error-boundary':
    case 'root-error-boundary':
    case 'window.onerror':
    case 'unhandledrejection':
    case 'chunk-load':
    case 'next-router-state':
    case 'react-310':
      return input;
    default:
      return 'unknown';
  }
}

function bumpAppRecoverCount(): number {
  try {
    const raw = safeLocalStorage.getItem('__hzc_app_recover_count__');
    const next = Math.min(999, (raw ? Number(raw) : 0) + 1);
    safeLocalStorage.setItem('__hzc_app_recover_count__', String(next));
    return Number.isFinite(next) ? next : 1;
  } catch {
    return 1;
  }
}

async function clearServiceWorkersAndCaches(): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      regs.forEach((reg) => {
        try {
          reg.unregister();
        } catch {}
      });
    }
  } catch {}

  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(
        keys.map((k) => {
          try {
            return caches.delete(k);
          } catch {
            return Promise.resolve(false);
          }
        }),
      );
    }
  } catch {}
}

function clearWebStorage(): void {
  safeLocalStorage.clear();
  safeSessionStorage.clear();
}

function navigateHomeWeb(): void {
  // cache-bust so the browser is forced to re-request HTML
  const target = `/?recover=1&t=${Date.now()}`;
  window.location.assign(target);
}

function navigateHomeApp(): void {
  // Best-effort: ask native shell to reset to home/first tab.
  // If native doesn't implement these actions, we fall back to a normal navigation.
  const ok =
    appRPC('bridge', 'resetTabs', null) ||
    appRPC('route', 'navigateWithUrl', { url: '/', target: 'home' }) ||
    appRPC('route', 'routeChanged', '/');

  if (!ok) {
    window.location.assign('/');
  }
}

export async function recoverToHome(
  reason: RecoverReason,
  options: RecoverOptions = {},
): Promise<void> {
  if (typeof window === 'undefined') return;

  const normalizedReason = normalizeReason(reason);

  const inApp = isAppClient();
  if (inApp && !options.force) {
    const count = bumpAppRecoverCount();
    if (count >= 3 && safeSessionStorage.getItem('__hzc_app_recover_modal_shown__') !== '1') {
      safeSessionStorage.setItem('__hzc_app_recover_modal_shown__', '1');
      try {
        window.dispatchEvent(
          new CustomEvent('hzc:recover:modal', { detail: { count, reason: normalizedReason } }),
        );
      } catch {}
      return;
    }
  }

  const recoverGuardKey = `__hzc_recover_to_home_${normalizedReason}__`;
  // Already tried this recovery once in this tab session — do NOT navigate again.
  // Re-assigning "/" on a persistent error (e.g. stale webpack in local dev) causes an infinite refresh loop.
  if (safeSessionStorage.getItem(recoverGuardKey) === '1') {
    return;
  }
  safeSessionStorage.setItem(recoverGuardKey, '1');

  if (normalizedReason === 'chunk-load') {
    const cleanup = clearServiceWorkersAndCaches();
    await Promise.race([cleanup, new Promise((r) => setTimeout(r, 1200))]);
    if (inApp) navigateHomeApp();
    else navigateHomeWeb();
    return;
  }

  clearWebStorage();
  safeSessionStorage.setItem(recoverGuardKey, '1');

  const cleanup = clearServiceWorkersAndCaches();
  await Promise.race([cleanup, new Promise((r) => setTimeout(r, 1200))]);

  if (inApp) navigateHomeApp();
  else navigateHomeWeb();
}
